import { StatsigProvider, useStatsigClient, useStatsigUser } from '@statsig/react-bindings';
import { merge } from 'lodash';
import { FC, useCallback } from 'react';
import { EnvConfig } from '../config/configMapping';
import { DefaultCurrentUserFragment } from '../graphql/generated/graphql';
import { logger } from '../initialisers/logger';
import { useEnvConfig } from './ConfigProvider';
import {
  DynamicConfigValues,
  dynamicConfigDefaultValues,
  dynamicConfigSchema,
} from './FeatureFlagProvider/defaultDynamicConfig';
import { FeatureFlagValues, featureFlagDefaultValues } from './FeatureFlagProvider/defaultFeatureFlags';

export const FeatureFlagProvider: FC<{ envConfig: EnvConfig; children: React.ReactNode }> = ({
  envConfig,
  children,
}) => {
  if (!envConfig.statsig.enabled) {
    return children;
  }

  /**
   * The user ID is not set here, this is set in the viewer provider.
   *
   * @see https://docs.statsig.com/concepts/user
   */
  return (
    <StatsigProvider sdkKey={envConfig.statsig.secretKey} user={{ userID: undefined }}>
      {children}
    </StatsigProvider>
  );
};

/**
 * This is used to overwrite the feature flags in the e2e environment
 */
declare let window: {
  ffOverwrites: Record<string, boolean> | undefined;
  myCustomProperty: string | undefined;
  ffOverwritesDynamicConfig: Record<string, DynamicConfigValues[keyof DynamicConfigValues]> | undefined;
};

/**
 * This hook is used to get the dynamic config from Statsig.
 *
 * @param configName - The name of the config to get
 * @param defaultValue - The default value to return if the config is not found
 * @returns The config value
 */
export function useDynamicConfig<T extends keyof DynamicConfigValues>(
  configName: T,
  defaultValue: DynamicConfigValues[T] = dynamicConfigDefaultValues[configName],
): DynamicConfigValues[T] {
  const envConfig = useEnvConfig();

  /**
   * This is used to overwrite the feature flags in the e2e environment
   */
  if (
    envConfig.environment === 'development' &&
    window.ffOverwritesDynamicConfig &&
    window.ffOverwritesDynamicConfig[configName]
  ) {
    return window.ffOverwritesDynamicConfig[configName];
  }

  if (!envConfig.statsig.enabled) {
    logger.debug(`Loading DC ${configName} from default`);
    return defaultValue;
  }

  // biome-ignore lint/correctness/useHookAtTopLevel: <This rule is no dynamic, therefore we don't need to have it at top level>
  const { client } = useStatsigClient();

  const config = client.getDynamicConfig(configName);

  const result = dynamicConfigSchema[configName].safeParse(merge(defaultValue, config.value));

  if (!result.success) {
    logger.debug(`Loading DC ${configName} failed parsing`);
    return defaultValue;
  }

  return result.data;
}

/**
 * This hook is used to get the dynamic config from Statsig.
 *
 * @param configName - The name of the config to get
 * @param defaultValue - The default value to return if the config is not found
 * @returns The config value
 */
export function useFeatureFlag<T extends keyof FeatureFlagValues>(
  configName: T,
  defaultValue: FeatureFlagValues[T] = featureFlagDefaultValues[configName],
): boolean {
  const envConfig = useEnvConfig();

  /**
   * Lets a Cypress spec force a gate, via `cy.setFeatureFlag`.
   *
   * The environment list has to name e2e: that is the environment the Cypress run actually boots under, so
   * gating on development alone meant every override was silently ignored and a spec about a gated flow
   * quietly exercised its ungated twin. Membership rather than truthiness, so a flag can be forced *off*
   * as well as on, which is how a spec pins the pre-gate behaviour.
   */
  if (
    (envConfig.environment === 'development' || envConfig.environment === 'e2e') &&
    window.ffOverwrites &&
    configName in window.ffOverwrites
  ) {
    return window.ffOverwrites[configName];
  }

  if (!envConfig.statsig.enabled) {
    logger.debug(`Loading FF ${configName} from default`);
    return defaultValue;
  }

  // biome-ignore lint/correctness/useHookAtTopLevel: <This rule is no dynamic, therefore we don't need to have it at top level>
  const { client } = useStatsigClient();

  const config = client.getFeatureGate(configName.toLowerCase());

  return config.value;
}

/**
 * This function is used to authenticate the user in Statsig.
 *
 * @param user - The user to authenticate
 * @returns True if the user is authenticated, false otherwise
 */
export const createFeatureFlagAuthUser = (): ((
  user: DefaultCurrentUserFragment,
  company: { id: string; name: string },
) => Promise<void>) => {
  const envConfig = useEnvConfig();

  if (!envConfig.statsig.enabled) {
    return () => Promise.resolve(void 0);
  }

  // biome-ignore lint/correctness/useHookAtTopLevel: <This rule is no dynamic, therefore we don't need to have it at top level>
  const { updateUserAsync } = useStatsigUser();

  // biome-ignore lint/correctness/useHookAtTopLevel: <This rule is no dynamic, therefore we don't need to have it at top level>
  return useCallback(
    // `company` is the one the session operates (the acting company while acting as one), not the user's own.
    async (user: DefaultCurrentUserFragment, company: { id: string; name: string }) => {
      await updateUserAsync({
        userID: user.id,
        custom: {
          companyID: company.id,
          companyName: company.name,
        },
      });
    },
    [updateUserAsync],
  );
};
